/**
 * InstallPwaButton — Bottone per installare la PWA nativamente.
 *
 * Viene mostrato solo quando:
 *  - Il browser ha emesso `beforeinstallprompt` (isInstallable = true)
 *  - L'app NON è già in modalità standalone (isInstalled = false)
 *
 * Su click, invoca il prompt di installazione nativo del browser.
 * Si nasconde automaticamente dopo l'installazione.
 */

import { Download, Loader2 } from 'lucide-react';
import { usePwaInstall } from '@/hooks/usePwaInstall';
import { useState } from 'react';

export function InstallPwaButton() {
  const { isInstallable, isInstalled, isPrompting, promptInstall } = usePwaInstall();
  const [showTooltip, setShowTooltip] = useState(false);

  // Non mostrare nulla se non installabile o già installata
  if (!isInstallable || isInstalled) {
    return null;
  }

  const handleClick = async () => {
    await promptInstall();
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={isPrompting}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-sm font-medium text-indigo-700 transition-colors hover:bg-indigo-100 active:bg-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label="Install app"
        title="Install app on your device"
      >
        {isPrompting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Download className="h-4 w-4" />
        )}
        <span className="hidden sm:inline">
          {isPrompting ? 'Installing...' : 'Install'}
        </span>
      </button>

      {/* Tooltip mobile/desktop */}
      {showTooltip && (
        <div className="absolute right-0 top-full mt-2 hidden w-48 rounded-md bg-gray-900 px-2 py-1.5 text-xs text-white sm:block shadow-lg z-50">
          Install this app on your device for quick access
          <div className="absolute -top-1 right-4 h-2 w-2 rotate-45 bg-gray-900" />
        </div>
      )}
    </div>
  );
}
